/**
 * Mermaid-Diagramme in gerendertem Markdown ersetzen.
 *
 * Mermaid wird erst geladen, wenn wirklich ein Diagramm vorkommt (eigener
 * Chunk). Syntaxfehler erzeugen KEINE Mermaid-eigene Fehlergrafik mehr
 * (`suppressErrorRendering`), sondern einen roten Banner in der Vorschau.
 */

import { getTheme } from "@/lib/theme";
import { hashSource } from "./tikz";
import { svgCached, svgLoad, svgStore } from "./svgCache";
import { dropStale, staleOf } from "./reuse";

let mermaidMod: Promise<any> | null = null;
let initializedFor = "";
let zenumlRegistered = false;

/** Gerenderte Diagramme je Quelle+Theme – verhindert Neuzeichnen bei jedem Tastendruck. */
const rendered = new Map<string, string>();

function loadMermaid(): Promise<any> {
  if (!mermaidMod) mermaidMod = import("mermaid");
  return mermaidMod;
}

/** Mermaid hängt Fehlerreste als lose <div>/<svg> an den Body – die räumen wir ab. */
function sweepStrayNodes(): void {
  document
    .querySelectorAll('body > div[id^="dmmd-"], body > div[id^="dmermaid-"], body > svg[id^="mmd-"]')
    .forEach((el) => el.remove());
}

function errorBanner(node: Element, message: string): void {
  const box = document.createElement("div");
  box.className = "md-diagram-error";
  const tag = document.createElement("span");
  tag.className = "md-diagram-error-tag";
  tag.textContent = "Mermaid";
  const msg = document.createElement("span");
  msg.className = "md-diagram-error-msg";
  msg.textContent = firstUsefulLine(message);
  box.append(tag, msg);
  const stale = staleOf(node);
  if (stale) {
    const hold = document.createElement("div");
    hold.className = "md-diagram-hold";
    if (node instanceof HTMLElement) copyLineAnchor(node, hold);
    hold.append(stale, box);
    node.replaceWith(hold);
    return;
  }
  if (node instanceof HTMLElement) {
    copyLineAnchor(node, box);
    box.style.minHeight = node.style.minHeight;
  }
  node.replaceWith(box);
}

/** Mermaid-Fehler sind mehrzeilig mit ASCII-Grafik – nur die Kernaussage zeigen. */
function firstUsefulLine(message: string): string {
  const clean = message.replace(/\s+$/, "");
  const line =
    clean
      .split("\n")
      .map((l) => l.trim())
      .find((l) => l && !/^[-^~\s]+$/.test(l) && !/^Parse error/i.test(l)) || clean;
  const expected = /Expecting[^\n]*/.exec(clean)?.[0];
  const text = expected && expected.length < 160 ? `${line} – ${expected}` : line;
  return text.slice(0, 240) || "Syntaxfehler im Diagramm.";
}

/**
 * Quelle eines Blocks, ergänzt um den Diagrammnamen, falls er nur als
 * Zaunsprache dastand (```xychart-beta statt einer ersten Zeile im Quelltext).
 */
function sourceOf(pre: HTMLElement, code: Element): string {
  const raw = code.textContent || "";
  const kind = pre.getAttribute("data-mermaid-kind");
  if (!kind) return raw;
  const first = raw.trimStart().split("\n", 1)[0]?.trim() ?? "";
  return first.startsWith(kind.split("-")[0]) ? raw : `${kind}\n${raw}`;
}

export async function hydrateMermaid(root: HTMLElement): Promise<void> {
  const blocks = Array.from(root.querySelectorAll("pre > code.language-mermaid"));
  if (!blocks.length) return;

  const theme = getTheme() === "dark" ? "dark" : "default";

  // Erst alles zeichnen, was schon vorliegt – und zwar BEVOR Mermaid geladen
  // wird. Nach einem Neuladen der Seite sind praktisch alle Diagramme im
  // dauerhaften Speicher (svgCache.ts); sie stehen dadurch sofort da, statt
  // erst auf den Mermaid-Chunk und dessen Rechenzeit zu warten.
  const pending: { pre: HTMLElement; source: string; key: string; hash: string }[] = [];
  for (const code of blocks) {
    const pre = code.parentElement as HTMLElement | null;
    if (!pre) continue;
    const source = sourceOf(pre, code);
    const key = theme + ":" + hashSource(source);
    const hash = pre.getAttribute("data-src-hash") ?? hashSource(source);

    const cached = rendered.get(key) ?? svgCached(`mmd:${key}`) ?? (await svgLoad(`mmd:${key}`));
    if (cached) {
      rendered.set(key, cached);
      dropStale(pre);
      pre.replaceWith(svgWrapper(cached, hash, pre));
      continue;
    }
    pending.push({ pre, source, key, hash });
  }
  if (!pending.length) return;

  let mermaid: any;
  try {
    mermaid = (await loadMermaid()).default;
  } catch {
    return; // Mermaid nicht ladbar -> Codeblock bleibt stehen
  }
  if (!zenumlRegistered && pending.some((p) => /^\s*zenuml\b/.test(p.source))) {
    const { default: zenuml } = await import("@mermaid-js/mermaid-zenuml");
    mermaid.registerExternalDiagrams([zenuml]);
    zenumlRegistered = true;
  }
  if (initializedFor !== theme) {
    mermaid.initialize({
      startOnLoad: false,
      theme,
      securityLevel: "loose",
      suppressErrorRendering: true,
      logLevel: "fatal",
    });
    initializedFor = theme;
    rendered.clear();
  }

  let i = 0;
  for (const { pre, source, key, hash } of pending) {
    try {
      // parse() wirft bei Syntaxfehlern, ohne etwas ins DOM zu schreiben.
      await mermaid.parse(source);
      const { svg } = await mermaid.render(`mmd-${Date.now()}-${i++}`, source);
      rendered.set(key, svg);
      void svgStore(`mmd:${key}`, svg);
      dropStale(pre);
      pre.replaceWith(svgWrapper(svg, hash, pre));
    } catch (err) {
      errorBanner(pre, (err as Error)?.message ?? String(err));
    }
  }
  sweepStrayNodes();
}

/**
 * `data-line`/`data-line-end` vom ersetzten `<pre>` übernehmen: ausgerechnet
 * Diagramme sind der Fall, für den der Mitscroll-Abgleich (syncScroll.ts)
 * einen Anker am dringendsten braucht – eine Quellzeile, hundert Pixel
 * Vorschau. Ohne Anker bekäme das nächste Element ihn fälschlich zugerechnet.
 */
function copyLineAnchor(from: HTMLElement, to: HTMLElement): void {
  const line = from.getAttribute("data-line");
  const end = from.getAttribute("data-line-end");
  if (line != null) to.setAttribute("data-line", line);
  if (end != null) to.setAttribute("data-line-end", end);
}

function svgWrapper(svg: string, hash: string, pre: HTMLElement): HTMLElement {
  const div = document.createElement("div");
  div.className = "md-mermaid";
  div.dataset.srcHash = hash;
  div.innerHTML = svg;
  copyLineAnchor(pre, div);
  return div;
}
